/**
 * ProcessEditor (editable-workspace-processes, PR3) — the board's edit-mode
 * panel + the shared dry-run confirm flow.
 *
 * Owns:
 *   - process settings: rename; (non-intake) visibility + roles, archive.
 *   - stage management: (non-intake) add / rename / reorder / phase + owner /
 *     deactivate + reactivate; (intake / is_system) LABEL RENAME ONLY — the
 *     stage set is locked, so add/reorder/deactivate affordances are absent
 *     (the server refuses them too).
 *   - the checklist template editor (toggled open).
 *   - askConfirm(): the ONE dry-run -> DryRunConfirmDialog -> commit path every
 *     consequential edit routes through, so the founder always sees the real
 *     server numbers first. Children (the template editor) call the same path.
 *
 * All writes go through useProcessCommand (invalidates the board + editor reads).
 */

import { useCallback, useState, type ReactNode } from 'react';
import { ChevronUp, ChevronDown, Pencil, Trash2, Plus } from 'lucide-react';
import { Button } from '@/components/primitives/Button';
import { TextAction } from '@/components/workspace/TextAction';
import { workspaceProcessesApi, type DryRunImpact, type WorkProcessWire, type WorkProcessStageWire, type WorkProcessVisibility } from '@/lib/api';
import { useProcessStages, useProcessCommand } from '@/lib/workspaceProcesses';
import { DryRunConfirmDialog } from './DryRunConfirmDialog';
import { ChecklistTemplateEditor } from './ChecklistTemplateEditor';
import { INTERNAL_ROLE_OPTIONS } from './NewProcessDialog';
import { contactSentence, type SupportContact } from '@/lib/errorCopy';
import { useSupportContact } from '@/hooks/useSupportContact';

/** A pending confirm: kicked off with impact=null, filled by the dry-run. */
export interface ConfirmSpec {
  title: string;
  intro?: string;
  confirmLabel: string;
  destructive?: boolean;
  dryRun: () => Promise<{ impact: DryRunImpact }>;
  commit: () => Promise<unknown>;
  /** Given the dry-run impact, return a blocking message (hides confirm) or null. */
  precheckBlock?: (impact: DryRunImpact) => string | null;
  /** Runs after a successful commit (e.g. the archive spec navigates away).
   *  Explicit — never inferred from labels. */
  afterCommit?: () => void;
}

export type AskConfirm = (spec: ConfirmSpec) => void;

interface PendingState {
  spec: ConfirmSpec;
  impact: DryRunImpact | null;
  blockingMessage: string | null;
}

export interface ProcessEditorProps {
  slug: string;
  process: WorkProcessWire;
  onExit: () => void;
  /** Called after the board is archived (the caller navigates away). */
  onArchived: () => void;
}

export function ProcessEditor({ slug, process, onExit, onArchived }: ProcessEditorProps): ReactNode {
  const contact = useSupportContact();
  const isSystem = process.is_system;
  const { stages, isLoading } = useProcessStages(slug, process.id);
  const command = useProcessCommand<unknown>(slug);
  const [pending, setPending] = useState<PendingState | null>(null);
  const [templateOpen, setTemplateOpen] = useState(false);
  const [addingStage, setAddingStage] = useState(false);
  const [newStageLabel, setNewStageLabel] = useState('');

  // The shared dry-run -> confirm -> commit path. Kicks off the dry-run to
  // fill the real numbers, then DryRunConfirmDialog renders them.
  const askConfirm = useCallback<AskConfirm>((spec) => {
    setPending({ spec, impact: null, blockingMessage: null });
    void spec
      .dryRun()
      .then((res) => {
        const block = spec.precheckBlock ? spec.precheckBlock(res.impact) : null;
        setPending((p) => (p && p.spec === spec ? { ...p, impact: res.impact, blockingMessage: block } : p));
      })
      .catch(() => {
        const message = contactSentence(
          "What this change affects couldn't be checked. Try again, and if it happens again, tell {contact_system}.",
          contact,
        );
        setPending((p) => (p && p.spec === spec ? { ...p, blockingMessage: message } : p));
      });
  }, [contact]);

  const runConfirm = (): void => {
    if (!pending) return;
    const spec = pending.spec;
    command.mutate(
      { run: () => spec.commit() },
      {
        onSuccess: () => {
          setPending(null);
          spec.afterCommit?.();
        },
        onError: (err) => {
          // Surface the server's refusal facts (occupied stage / open cards).
          setPending((p) => (p ? { ...p, blockingMessage: serverFacts(err, contact) } : p));
        },
      },
    );
  };

  const active = stages.filter((s) => s.is_active);
  const inactive = stages.filter((s) => !s.is_active);

  return (
    <section className="mb-5 rounded-2xl border border-warm-200 bg-paper p-4" data-testid="process-editor">
      <div className="flex items-center gap-3 mb-3">
        <h3 className="font-display text-[15px] font-semibold text-sage-800">Edit this board</h3>
        <TextAction className="ml-auto" data-testid="process-editor-done" onClick={onExit}>Done editing</TextAction>
      </div>

      <ProcessSettings slug={slug} process={process} askConfirm={askConfirm} onArchived={onArchived} />

      {/* Stages */}
      <div className="mt-4">
        <div className="text-xs uppercase tracking-wider text-sage-400 font-bold mb-2">Stages</div>
        {isLoading ? (
          <p className="text-sm text-ink-soft">Loading…</p>
        ) : (
          <div className="space-y-1.5" data-testid="process-stage-list">
            {active.map((stage, i) => (
              <StageRow
                key={stage.id}
                slug={slug}
                stage={stage}
                index={i}
                total={active.length}
                isSystem={isSystem}
                askConfirm={askConfirm}
              />
            ))}
          </div>
        )}

        {/* Add stage — non-intake only (the intake stage set is locked). */}
        {!isSystem && (
          addingStage ? (
            <form
              className="mt-2 flex items-center gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                const label = newStageLabel.trim();
                if (!label) return;
                command.mutate(
                  { run: () => workspaceProcessesApi.createStage(slug, process.id, { label }) },
                  { onSuccess: () => { setAddingStage(false); setNewStageLabel(''); } },
                );
              }}
            >
              <input
                autoFocus
                value={newStageLabel}
                onChange={(e) => setNewStageLabel(e.target.value)}
                data-testid="add-stage-label"
                placeholder="New stage name"
                className="flex-1 rounded-base border border-sage-200 px-2 py-1.5 text-sm"
              />
              <Button size="sm" type="submit" data-testid="add-stage-submit" disabled={command.isPending || newStageLabel.trim() === ''}>Add</Button>
              <TextAction onClick={() => { setAddingStage(false); setNewStageLabel(''); }}>Cancel</TextAction>
            </form>
          ) : (
            <Button
              variant="ghost"
              size="sm"
              icon={<Plus size={14} aria-hidden="true" />}
              data-testid="add-stage-open"
              onClick={() => setAddingStage(true)}
              className="mt-2"
            >
              Add stage
            </Button>
          )
        )}

        {/* Inactive (deactivated) stages — reactivatable. Non-intake only. */}
        {!isSystem && inactive.length > 0 && (
          <div className="mt-3" data-testid="process-inactive-stages">
            <div className="text-[11px] uppercase tracking-wider text-sage-400 font-semibold mb-1">Deactivated</div>
            {inactive.map((stage) => (
              <div key={stage.id} className="flex items-center gap-2 py-1 text-[13px] text-ink-soft">
                <span className="flex-1 line-through">{stage.label}</span>
                <TextAction
                  data-testid={`reactivate-stage-${stage.key}`}
                  onClick={() => command.mutate({ run: () => workspaceProcessesApi.reactivateStage(slug, stage.id) })}
                >
                  Reactivate
                </TextAction>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Checklist template editor */}
      <div className="mt-4 pt-3 border-t border-sage-200">
        <Button
          variant="ghost"
          size="sm"
          icon={<Pencil size={13} aria-hidden="true" />}
          data-testid="open-template-editor"
          onClick={() => setTemplateOpen(true)}
        >
          Edit the checklist steps
        </Button>
      </div>

      {templateOpen && process.template_id && (
        <ChecklistTemplateEditor
          slug={slug}
          process={process}
          activeStages={active}
          askConfirm={askConfirm}
          onClose={() => setTemplateOpen(false)}
        />
      )}

      {pending && (
        <DryRunConfirmDialog
          title={pending.spec.title}
          intro={pending.spec.intro}
          impact={pending.impact}
          confirmLabel={pending.spec.confirmLabel}
          destructive={pending.spec.destructive}
          blockingMessage={pending.blockingMessage}
          submitting={command.isPending}
          onConfirm={runConfirm}
          onCancel={() => setPending(null)}
        />
      )}
    </section>
  );
}

/** Pull the server refusal facts off an ApiError body into a sentence. */
function serverFacts(err: unknown, contact: SupportContact): string {
  const body = (err as { body?: unknown })?.body;
  if (body && typeof body === 'object') {
    const b = body as Record<string, unknown>;
    if (b['error'] === 'stage_has_open_cards') {
      const count = Number(b['open_card_count'] ?? 0);
      const titles = Array.isArray(b['sample_titles']) ? (b['sample_titles'] as string[]) : [];
      const names = titles.length > 0 ? ` (${titles.join(', ')}${count > titles.length ? ', …' : ''})` : '';
      return `${count} open card${count === 1 ? '' : 's'} still sit here${names}. Move them first.`;
    }
    if (b['error'] === 'process_has_open_cards') {
      const count = Number(b['open_card_count'] ?? 0);
      return `${count} open card${count === 1 ? '' : 's'} are still on this board. Move or close them first.`;
    }
  }
  return contactSentence("That didn't save, and nothing changed. Try again, and if it happens again, tell {contact_system}.", contact);
}

// ---------------------------------------------------------------------
// Process settings (rename / visibility+roles / archive)
// ---------------------------------------------------------------------

function ProcessSettings({ slug, process, askConfirm, onArchived }: { slug: string; process: WorkProcessWire; askConfirm: AskConfirm; onArchived: () => void }): ReactNode {
  const command = useProcessCommand<unknown>(slug);
  const [label, setLabel] = useState(process.label);
  const isSystem = process.is_system;

  const saveLabel = (): void => {
    const v = label.trim();
    if (v === '' || v === process.label) return;
    command.mutate({ run: () => workspaceProcessesApi.updateProcess(slug, process.id, { label: v }) });
  };

  const [visibility, setVisibility] = useState<WorkProcessVisibility>(process.visibility);
  const [roles, setRoles] = useState<Set<string>>(new Set(process.allowed_roles));
  const restricted = visibility === 'restricted';
  const visibilityDirty =
    visibility !== process.visibility ||
    (restricted && [...roles].sort().join(',') !== [...process.allowed_roles].sort().join(','));

  const saveVisibility = (): void => {
    if (restricted && roles.size === 0) return;
    command.mutate({
      run: () => workspaceProcessesApi.updateProcess(slug, process.id, { visibility, allowed_roles: restricted ? [...roles] : [] }),
    });
  };

  const toggleRole = (r: string): void =>
    setRoles((prev) => { const next = new Set(prev); next.has(r) ? next.delete(r) : next.add(r); return next; });

  return (
    <div className="space-y-3" data-testid="process-settings">
      <label className="block text-sm text-ink">
        Board name
        <span className="mt-1 flex items-center gap-2">
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={saveLabel}
            onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            data-testid="process-rename-input"
            className="flex-1 rounded-base border border-sage-200 px-2 py-1.5 text-sm"
          />
        </span>
      </label>

      {/* Visibility + archive — non-intake only (intake stays all_staff, never archived). */}
      {!isSystem && (
        <>
          <fieldset data-testid="process-visibility">
            <legend className="text-sm text-ink mb-1">Who can see this board</legend>
            <label className="flex items-center gap-2 text-sm text-ink mb-1">
              <input type="radio" name={`vis-${process.id}`} checked={!restricted} onChange={() => setVisibility('all_staff')} data-testid="process-visibility-all" />
              Everyone
            </label>
            <label className="flex items-center gap-2 text-sm text-ink">
              <input type="radio" name={`vis-${process.id}`} checked={restricted} onChange={() => setVisibility('restricted')} data-testid="process-visibility-restricted" />
              Only certain roles
            </label>
            {restricted && (
              <div className="mt-1.5 pl-1 space-y-1" data-testid="process-roles">
                {INTERNAL_ROLE_OPTIONS.map((r) => (
                  <label key={r.value} className="flex items-center gap-2 text-sm text-ink">
                    <input type="checkbox" checked={roles.has(r.value)} onChange={() => toggleRole(r.value)} data-testid={`process-role-${r.value}`} />
                    {r.label}
                  </label>
                ))}
              </div>
            )}
            {visibilityDirty && (
              <div className="mt-2">
                <Button size="sm" variant="secondary" data-testid="process-visibility-save" disabled={command.isPending || (restricted && roles.size === 0)} onClick={saveVisibility}>
                  Save visibility
                </Button>
              </div>
            )}
          </fieldset>

          <div className="pt-1">
            <button
              type="button"
              data-testid="process-archive"
              onClick={() =>
                askConfirm({
                  title: 'Archive this board?',
                  intro: `“${process.label}” will be hidden from the board switcher. You can restore it later.`,
                  confirmLabel: 'Archive board',
                  destructive: true,
                  dryRun: () => workspaceProcessesApi.archiveProcess(slug, process.id, true),
                  commit: () => workspaceProcessesApi.archiveProcess(slug, process.id),
                  afterCommit: onArchived,
                  precheckBlock: (impact) =>
                    impact.open_cards_affected > 0
                      ? `${impact.open_cards_affected} open card${impact.open_cards_affected === 1 ? '' : 's'} ${impact.open_cards_affected === 1 ? 'is' : 'are'} still on this board. Move or close ${impact.open_cards_affected === 1 ? 'it' : 'them'} first.`
                      : null,
                })
              }
              className="inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-xs font-semibold text-pepper transition-colors duration-150 hover:bg-pepper-soft focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400"
            >
              <Trash2 size={13} aria-hidden="true" /> Archive board
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------
// A single stage row (rename + phase/owner + reorder + deactivate)
// ---------------------------------------------------------------------

function StageRow({
  slug, stage, index, total, isSystem, askConfirm,
}: {
  slug: string; stage: WorkProcessStageWire; index: number; total: number; isSystem: boolean; askConfirm: AskConfirm;
}): ReactNode {
  const command = useProcessCommand<unknown>(slug);
  const [label, setLabel] = useState(stage.label);

  const saveLabel = (): void => {
    const v = label.trim();
    if (v === '' || v === stage.label) return;
    command.mutate({ run: () => workspaceProcessesApi.updateStage(slug, stage.id, { label: v }) });
  };
  const move = (dir: -1 | 1): void => {
    const target = index + 1 + dir; // 1-based target position
    if (target < 1 || target > total) return;
    command.mutate({ run: () => workspaceProcessesApi.updateStage(slug, stage.id, { sort_order: target }) });
  };

  return (
    <div className="flex items-center gap-2" data-testid={`stage-row-${stage.key}`}>
      {/* Reorder — non-intake only (the intake stage ORDER is locked). */}
      {!isSystem && (
        <div className="flex flex-col">
          <button type="button" aria-label={`Move ${stage.label} earlier`} data-testid={`stage-up-${stage.key}`} disabled={index === 0 || command.isPending} onClick={() => move(-1)}
            className="text-sage-400 hover:text-sage-800 disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded">
            <ChevronUp size={14} aria-hidden="true" />
          </button>
          <button type="button" aria-label={`Move ${stage.label} later`} data-testid={`stage-down-${stage.key}`} disabled={index === total - 1 || command.isPending} onClick={() => move(1)}
            className="text-sage-400 hover:text-sage-800 disabled:opacity-30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded">
            <ChevronDown size={14} aria-hidden="true" />
          </button>
        </div>
      )}
      <input
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        onBlur={saveLabel}
        onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
        aria-label={`Rename ${stage.label}`}
        data-testid={`stage-label-${stage.key}`}
        className="flex-1 rounded-base border border-sage-200 px-2 py-1 text-[13px]"
      />
      {/* Deactivate — non-intake only; the server refuses while the stage is occupied. */}
      {!isSystem && (
        <button
          type="button"
          data-testid={`stage-deactivate-${stage.key}`}
          onClick={() =>
            askConfirm({
              title: 'Remove this stage?',
              intro: `“${stage.label}” will drop off the board.`,
              confirmLabel: 'Remove stage',
              destructive: true,
              dryRun: () => workspaceProcessesApi.deactivateStage(slug, stage.id, true),
              commit: () => workspaceProcessesApi.deactivateStage(slug, stage.id),
              precheckBlock: (impact) =>
                impact.open_cards_affected > 0
                  ? `${impact.open_cards_affected} open card${impact.open_cards_affected === 1 ? '' : 's'} ${impact.open_cards_affected === 1 ? 'sits' : 'sit'} in this stage. Move ${impact.open_cards_affected === 1 ? 'it' : 'them'} first.`
                  : null,
            })
          }
          className="text-sage-400 hover:text-pepper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sage-400 rounded p-1"
          aria-label={`Remove ${stage.label}`}
        >
          <Trash2 size={14} aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
